// components/common/Footer.js - Optimized
import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "../../services/Api";
import logoWhite from "../../assets/images/lomahuuto-fi-logo-white-1024.png";

// Cache for areas - shared across all Footer instances
let areasCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const AREA_PLACEHOLDER_COUNT = 15;

const Footer = () => {
    const [areas, setAreas] = useState([]);
    const [loadingAreas, setLoadingAreas] = useState(true);
    const areaPlaceholders = useMemo(
        () => Array.from({ length: AREA_PLACEHOLDER_COUNT }, (_, index) => index),
        [],
    );

    useEffect(() => {
        const fetchAreas = async () => {
            // Check if we have valid cached data
            const now = Date.now();
            if (areasCache && cacheTimestamp && now - cacheTimestamp < CACHE_DURATION) {
                setAreas(areasCache);
                setLoadingAreas(false);
                return;
            }

            setLoadingAreas(true);
            try {
                const response = await api.getAvailableAreasAuctions();

                // Update cache
                areasCache = response;
                cacheTimestamp = Date.now();

                setAreas(response);
            } catch (error) {
                console.error("Error fetching areas:", error);
            } finally {
                setLoadingAreas(false);
            }
        };

        fetchAreas();
    }, []);

    /* Ulkomaankohteet omaksi ryhmäkseen (Mika 7.10.2026): aakkosjärjestyksessä ne hukkuivat
       kotimaisten sekaan. Kohteet: Åre, Costa Del Sol, Teneriffa, Gran Canaria. */
    const onUlkomaa = (area) =>
        ["åre", "costa del sol", "teneriffa", "gran canaria"].includes(String(area.area || "").trim().toLowerCase());

    const alueLinkki = (area) => (
        <Link
            to={`/kohteet?t=sale&filter_areas=${area.area}`}
            key={area.slug}
            className="text-white text-sm md:text-base hover:text-primary-100 transition-colors"
        >
            {area.area}
        </Link>
    );

    // Memoize the area links to prevent re-renders
    const areaLinks = useMemo(() => {
        if (loadingAreas) {
            return areaPlaceholders.map((placeholder) => (
                <div key={placeholder} className="h-5 rounded bg-white/20 animate-pulse" aria-hidden="true" />
            ));
        }

        return areas.filter((area) => !onUlkomaa(area)).map(alueLinkki);
    }, [areas, loadingAreas]);

    const ulkomaaLinks = useMemo(
        () => (loadingAreas ? [] : areas.filter(onUlkomaa).map(alueLinkki)),
        [areas, loadingAreas],
    );

    return (
        <footer className="p-8 bg-primary-700 mt-5">
            <div className="flex gap-10 max-w-screen-2xl m-auto text-lg md:p-5 md:px-0 flex-col xl:flex-row">
                <div className="order-2 lg:order-1 text-center md:text-left">
                    <div className="flex justify-center md:justify-start mb-5">
                        <img
                            src={logoWhite}
                            alt="Lomahuuto.fi"
                            className="w-64"
                            loading="lazy"
                            width="1024"
                            height="149"
                        />
                    </div>
                    <div className="text-white text-sm md:text-base mt-5">
                        Lomahuuto.fi on Suomen kätevin lomaosakkeiden myynti- ja vuokrauspalvelu.
                        <br />
                        <span className="font-bold">Lomahuuto.fi</span> on luotettava ja turvallinen alusta, joka
                        yhdistää myyjät ja ostajat helposti ja nopeasti.
                    </div>
                    <div className="text-white text-sm md:text-base mt-5">
                        Etsitkö omistusasuntoa Espanjasta? Yhteistyökumppanimme kautta{" "}
                        <a
                            href="https://costankodit.fi"
                            target="_blank"
                            rel="noopener"
                            className="underline underline-offset-2 hover:text-primary-100"
                        >
                            yli 6&nbsp;000 asuntokohdetta Aurinkorannikolla
                        </a>
                        .
                    </div>
                    <div className="text-white text-sm md:text-base mt-2">
                        Yhteistyökumppanimme{" "}
                        <a
                            href="https://bidmytimeshare.com/en"
                            target="_blank"
                            rel="noopener"
                            className="underline underline-offset-2 hover:text-primary-100"
                        >
                            BidMyTimeshare.com
                        </a>{" "}
                        on kansainvälinen lomaosakkeiden markkinapaikka.
                    </div>
                    <div className="text-primary-100 font-bold text-sm mt-5">
                        Copyright &copy; {new Date().getFullYear()} Lomahuuto.fi. All rights reserved.
                    </div>
                </div>
                <div className="flex-1 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 order-1 xl:order-2 min-h-24 md:min-h-32 content-start">
                    {areaLinks}
                    {ulkomaaLinks.length > 0 && (
                        <>
                            <div className="col-span-full mt-3 text-primary-100 font-bold text-sm">
                                Ulkomaat
                            </div>
                            {ulkomaaLinks}
                        </>
                    )}
                </div>
            </div>
        </footer>
    );
};

// Export with memo to prevent unnecessary re-renders
export default React.memo(Footer);
